import React from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App";
import "./i18n";
import "./styles/tailwind.css";
import "./styles/index.css";

// Aplicar zoom guardado antes de montar React para que también afecte
// rutas públicas (/login, landing) y evitar flash en cada navegación.
try {
    const z = parseInt(localStorage.getItem('pm_zoom_level'), 10);
    if (!Number.isNaN(z) && z >= 50 && z <= 200 && z !== 100) {
        document.documentElement.style.zoom = (z / 100).toString();
    }
} catch (_) { /* noop */ }


// Si el usuario tenía la pestaña abierta antes de un deploy y los chunks
// viejos ya no existen, los lazy imports fallan. Recargamos para tomar el
// index.html nuevo. Redes de seguridad, en orden de confianza:
//   1) vite:preloadError — evento oficial de Vite (happy path).
//   2) unhandledrejection / error — atrapa la promise rechazada del import()
//      cuando escapa al wrapper de Vite. Cubre mensajes de Chrome, Firefox,
//      Safari desktop, iOS Safari/Chrome (WebKit dice "Load failed" o
//      "The module '.../assets/x.js' could not be loaded/resolved").
//      Fallback por stack: si el error trae "/assets/*.js" en el stack o
//      filename, es casi seguro un chunk aunque el message no matchee.
//   3) ErrorBoundary.componentDidCatch consulta window.__isChunkLoadError__
//      para recargar en vez de mostrar la pantalla de error.
//   4) Red final: aún si nada de lo anterior matchea, si el error viene de
//      un contexto sospechoso disparamos un check de /version.json — si
//      hay un deploy nuevo, reload sin depender de matchear el mensaje.
// Un guard de sessionStorage previene loops si el chunk sigue faltando
// después del reload (deploy roto).
const CHUNK_ERROR_RE = /Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Load failed|Failed to load module|module.*could not be (loaded|resolved)|module.*cannot be (loaded|resolved)|Unable to preload/i;
const ASSET_PATH_RE = /\/assets\/[^\s"']+\.(js|mjs)/i;
window.__isChunkLoadError__ = (err) => {
    if (!err) return false;
    const msg = (err.message || err.toString?.() || '') + '';
    if (CHUNK_ERROR_RE.test(msg)) return true;
    const stack = (err.stack || '') + '';
    if (ASSET_PATH_RE.test(stack) || ASSET_PATH_RE.test(msg)) return true;
    const filename = err.filename || err.fileName;
    if (filename && ASSET_PATH_RE.test(filename)) return true;
    return false;
};
const reloadForChunkError = () => {
    try {
        const RELOAD_FLAG = 'pm_chunk_reload_at';
        const last = parseInt(sessionStorage.getItem(RELOAD_FLAG) || '0', 10);
        if (Date.now() - last < 10_000) return; // ya recargamos hace menos de 10s, evitar loop
        sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
    } catch (_) { /* noop */ }
    window.location.reload();
};
// Última red: chequeo inmediato de /version.json cuando algo huele a chunk
// pero no matcheamos por certeza. Si el server tiene un build nuevo, reload.
window.__reloadIfDeployChanged__ = async () => {
    try {
        const RELOAD_FLAG = 'pm_chunk_reload_at';
        const last = parseInt(sessionStorage.getItem(RELOAD_FLAG) || '0', 10);
        if (Date.now() - last < 10_000) return; // guard anti-loop compartido
        const res = await fetch('/version.json?t=' + Date.now(), { cache: 'no-store' });
        if (!res.ok) return;
        const { version } = await res.json();
        const stored = localStorage.getItem('pm_app_version');
        if (stored && version && stored !== version) {
            sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
            localStorage.setItem('pm_app_version', version);
            window.location.reload();
        }
    } catch (_) { /* noop */ }
};
window.addEventListener('vite:preloadError', reloadForChunkError);
window.addEventListener('unhandledrejection', (e) => {
    if (window.__isChunkLoadError__(e.reason)) { reloadForChunkError(); return; }
    // El error escapó a los patrones conocidos — probamos el chequeo de deploy.
    if (e.reason) window.__reloadIfDeployChanged__();
});
window.addEventListener('error', (e) => {
    if (window.__isChunkLoadError__(e.error)) { reloadForChunkError(); return; }
    // Errores globales de <script>/module tags también son sospechosos.
    if (e.filename && ASSET_PATH_RE.test(e.filename)) window.__reloadIfDeployChanged__();
});

// Detecta deploys nuevos comparando version.json con la versión cacheada.
// Si hay una versión nueva, fuerza un hard reload para que el usuario
// siempre use el JS más reciente sin necesidad de limpiar caché manualmente.
const VERSION_KEY = 'pm_app_version';
fetch('/version.json?t=' + Date.now())
    .then(r => r.json())
    .then(({ version }) => {
        const stored = localStorage.getItem(VERSION_KEY);
        if (stored && stored !== version) {
            localStorage.setItem(VERSION_KEY, version);
            window.location.reload(true);
        } else {
            localStorage.setItem(VERSION_KEY, version);
        }
    })
    .catch(() => { /* silencioso si no hay version.json */ });

const container = document.getElementById("root");
const root = createRoot(container);

root.render(
  <HelmetProvider>
    <App />
  </HelmetProvider>
);
